import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { News, Category, PaginatedResponse } from '../../types/api';
import { newsApi, categoriesApi, NewsApiParams } from '../../services/api';

interface Pagination {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  pageSize: number;
}

interface NewsState {
  items: News[];
  currentItem: News | null;
  categories: Category[];
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null;
  pagination: Pagination;
  editorPicks: News[];
  topRated: News[];
  relatedNews: News[];
}

const initialState: NewsState = {
  items: [],
  currentItem: null,
  categories: [],
  status: 'idle',
  error: null,
  pagination: {
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    pageSize: 10
  },
  editorPicks: [],
  topRated: [],
  relatedNews: []
};

// Async actions
export const fetchNews = createAsyncThunk(
  'news/fetchNews',
  async (params: NewsApiParams = { page: 1, page_size: 10 }, { rejectWithValue }) => {
    try {
      const response = await newsApi.getNews(params);
      return {
        items: response.data.results,
        pagination: {
          currentPage: Number(response.data.current) || 1,
          totalPages: Number(response.data.pages) || 1,
          totalItems: Number(response.data.count) || 0,
          pageSize: Number(params.page_size) || 10
        }
      };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Ошибка при загрузке новостей');
    }
  }
);

export const fetchNewsByCategory = createAsyncThunk(
  'news/fetchNewsByCategory',
  async (
    { slug, page = 1, page_size = 9 }: { slug: string; page?: number; page_size?: number },
    { rejectWithValue }
  ) => {
    try {
      const response = await newsApi.getNewsByCategory(slug, {
        page,
        page_size
      });
      
      return {
        items: response.data.results,
        pagination: {
          currentPage: Number(page ?? 1),
          totalPages: Math.ceil(Number(response.data.count ?? 0) / Number(page_size ?? 10)) || 1,
          totalItems: Number(response.data.count ?? 0),
          pageSize: Number(page_size ?? 10)
        }
      };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Ошибка при загрузке новостей категории');
    }
  }
);

export const fetchNewsById = createAsyncThunk(
  'news/fetchNewsById',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await newsApi.getNewsById(id);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Не удалось загрузить новость');
    }
  }
);

export const fetchCategories = createAsyncThunk(
  'news/fetchCategories',
  async (_, { rejectWithValue }) => {
    try {
      const response = await categoriesApi.getCategories();
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Не удалось загрузить категории');
    }
  }
);

export const fetchEditorPicks = createAsyncThunk(
  'news/fetchEditorPicks',
  async (_, { rejectWithValue }) => {
    try {
      const response = await newsApi.getEditorPicks();
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Не удалось загрузить избранные новости');
    }
  }
);

export const fetchTopRated = createAsyncThunk(
  'news/fetchTopRated',
  async (_, { rejectWithValue }) => {
    try {
      const response = await newsApi.getTopRated();
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Не удалось загрузить топ новостей');
    }
  }
);

export const fetchUserNews = createAsyncThunk(
  'news/fetchUserNews',
  async (userId: number, { rejectWithValue }) => {
    try {
      const response = await newsApi.getUserNews(userId);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Не удалось загрузить новости пользователя');
    }
  }
);

export const fetchRelatedNews = createAsyncThunk(
  'news/fetchRelatedNews',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await newsApi.getRelatedNews(id);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Не удалось загрузить похожие новости');
    }
  }
);

const newsSlice = createSlice({
  name: 'news',
  initialState,
  reducers: {
    clearCurrentItem: (state) => {
      state.currentItem = null;
    },
    clearError: (state) => {
      state.error = null;
      state.status = 'idle';
    },
  },
  extraReducers: (builder) => {
    // Загрузка новостей
    builder
      .addCase(fetchNews.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchNews.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.items = action.payload.items;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchNews.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload as string || 'Не удалось загрузить новости';
      })

      // Загрузка новостей по категории
      .addCase(fetchNewsByCategory.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchNewsByCategory.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.items = action.payload.items;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchNewsByCategory.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload as string || 'Не удалось загрузить новости категории';
      })
      .addCase(fetchNewsById.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchNewsById.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.currentItem = action.payload;
      })
      .addCase(fetchNewsById.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload as string || 'Не удалось загрузить новость';
      })
      .addCase(fetchEditorPicks.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchEditorPicks.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.editorPicks = action.payload;
      })
      .addCase(fetchEditorPicks.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload as string || 'Не удалось загрузить избранные новости';
      })
      .addCase(fetchTopRated.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchTopRated.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.topRated = action.payload;
      })
      .addCase(fetchTopRated.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload as string || 'Не удалось загрузить топ новостей';
      })
      .addCase(fetchRelatedNews.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchRelatedNews.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.relatedNews = action.payload;
      })
      .addCase(fetchRelatedNews.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload as string || 'Не удалось загрузить похожие новости';
      })
      .addCase(fetchUserNews.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchUserNews.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.items = action.payload; // getUserNews returns News[] directly
        state.pagination = {
          currentPage: 1,
          totalPages: 1,
          totalItems: action.payload.length,
          pageSize: action.payload.length
        };
      })
      .addCase(fetchUserNews.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload as string || 'Не удалось загрузить новости пользователя';
      })
      .addCase(fetchCategories.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchCategories.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.categories = action.payload;
      })
      .addCase(fetchCategories.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload as string || 'Не удалось загрузить категории';
      });
  },
});

export const { clearCurrentItem, clearError } = newsSlice.actions;
export default newsSlice.reducer;
